import React, { useMemo, useState, useEffect } from 'react'
import { X, Package } from 'lucide-react'
import type { ProductNameGroup } from '../../utils/productGrouping'
import type { ProductColor, ProductSize } from '../../utils/productGrouping'
import { getSortedSizeColumns } from '../../utils/productGrouping'
import { StockBadge } from './ui/stock-badge'
import { QuantityStepper } from '../ui/QuantityStepper'
import { Button } from '../ui/Button'

export interface VariantAddSelection {
  productId: string
  quantity: number
}

interface ProductVariantMatrixModalProps {
  group: ProductNameGroup
  colors: ProductColor[]
  sizes: ProductSize[]
  showPricing: boolean
  showOutOfStock: boolean
  onClose: () => void
  onAddVariants: (selections: VariantAddSelection[]) => void
  getLocationQty: (productId: string) => number
  getAvailableQty: (productId: string) => number
  getReservedQty: (productId: string) => number
  ignoreStock?: boolean
  /** When true, hide stock/reserved badges (used by stock count). Defaults false. */
  hideStockInfo?: boolean
}

export function ProductVariantMatrixModal({
  group,
  colors,
  sizes,
  showPricing,
  showOutOfStock,
  onClose,
  onAddVariants,
  getLocationQty,
  getAvailableQty,
  getReservedQty,
  ignoreStock = false,
  hideStockInfo = false
}: ProductVariantMatrixModalProps) {
  const [cellQuantities, setCellQuantities] = useState<Record<string, number>>({})

  const sizeColumns = useMemo(
    () => getSortedSizeColumns([group], sizes),
    [group, sizes]
  )

  const getCellKey = (colorId: string, sizeName: string) => `${colorId}::${sizeName}`

  const getProductIdForCell = (colorGroup: ProductNameGroup['colors'][0], sizeName: string): string | null => {
    return colorGroup.sizeItemIds[sizeName] || null
  }

  const handleCellQtyChange = (key: string, qty: number, maxAvailable: number) => {
    setCellQuantities(prev => ({
      ...prev,
      [key]: Math.min(Math.max(0, qty), maxAvailable)
    }))
  }

  const selections = useMemo(() => {
    const result: VariantAddSelection[] = []
    for (const colorGroup of group.colors) {
      for (const sizeName of sizeColumns) {
        const productId = getProductIdForCell(colorGroup, sizeName)
        if (!productId) continue
        const key = getCellKey(colorGroup.colorId, sizeName)
        const qty = cellQuantities[key] || 0
        if (qty > 0) result.push({ productId, quantity: qty })
      }
    }
    return result
  }, [group.colors, sizeColumns, cellQuantities])

  const totalSelectedQty = selections.reduce((sum, s) => sum + s.quantity, 0)
  const totalSelectedValue = useMemo(() => {
    if (!showPricing) return 0
    return selections.reduce((sum, sel) => {
      const variant = group.variants.find(v => v.id === sel.productId)
      const price = variant?.sell_price ?? variant?.unit_price ?? 0
      return sum + price * sel.quantity
    }, 0)
  }, [selections, group.variants, showPricing])

  const handleBulkAdd = () => {
    if (selections.length === 0) return
    onAddVariants(selections)
    setCellQuantities({})
    onClose()
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 bg-gray-900/70 backdrop-blur-sm overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="variant-matrix-title"
    >
      <div className="relative w-full max-w-4xl my-4 sm:my-8 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh]">
        <div className="sticky top-0 z-10 flex items-start gap-4 p-5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-t-2xl">
          <div className="h-16 w-16 rounded-xl bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
            {group.imageUrl ? (
              <img src={group.imageUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center">
                <Package className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="variant-matrix-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
              {group.productName}
            </h2>
            {group.category && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{group.category}</p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {group.variantCount} variants ·{' '}
              {group.totalQuantity > 0 ? (
                <span className="font-medium text-green-600 dark:text-green-400">{group.totalQuantity} in stock</span>
              ) : (
                <span className="font-medium text-red-600 dark:text-red-400">Out of stock</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700 min-w-[120px]">
                    Color
                  </th>
                  {sizeColumns.map(sizeName => (
                    <th
                      key={sizeName}
                      className="px-2 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700 min-w-[100px]"
                    >
                      {sizeName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.colors.map(colorGroup => (
                  <tr key={colorGroup.colorId} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 px-3 py-3 border-r border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-2">
                        {colorGroup.colorId !== 'no-color' && (
                          <span
                            className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 flex-shrink-0"
                            style={{ backgroundColor: colorGroup.hexCode || '#ccc' }}
                          />
                        )}
                        <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[100px]">
                          {colorGroup.colorName}
                        </span>
                      </div>
                    </td>
                    {sizeColumns.map(sizeName => {
                      const productId = getProductIdForCell(colorGroup, sizeName)
                      if (!productId) {
                        return (
                          <td key={sizeName} className="px-2 py-3 text-center text-gray-300 dark:text-gray-600">
                            —
                          </td>
                        )
                      }

                      const locationQty = getLocationQty(productId)
                      const availableQty = getAvailableQty(productId)
                      const reservedQty = getReservedQty(productId)
                      const isOutOfStock = availableQty <= 0
                      const cellMaxQty = ignoreStock ? 9999 : availableQty

                      if (!showOutOfStock && !ignoreStock && isOutOfStock) {
                        return (
                          <td key={sizeName} className="px-2 py-3 text-center text-gray-200 dark:text-gray-700">
                            ·
                          </td>
                        )
                      }

                      const key = getCellKey(colorGroup.colorId, sizeName)
                      const cellQty = cellQuantities[key] || 0
                      const variant = group.variants.find(v => v.id === productId)
                      const price = variant?.sell_price ?? variant?.unit_price

                      return (
                        <td key={sizeName} className="px-2 py-3 align-top">
                          <div
                            className="flex flex-col items-center gap-2 min-w-[88px]"
                            aria-label={`${colorGroup.colorName} size ${sizeName}, ${availableQty} available`}
                          >
                            {!hideStockInfo && (
                              isOutOfStock ? (
                                <StockBadge quantity={0} showLabel size="sm" />
                              ) : (
                                <StockBadge quantity={locationQty} size="sm" />
                              )
                            )}
                            {!hideStockInfo && reservedQty > 0 && !isOutOfStock && (
                              <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                {reservedQty} reserved
                              </span>
                            )}
                            {showPricing && price != null && price > 0 && (
                              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                {'\u20AC'}{price.toFixed(2)}
                              </span>
                            )}
                            {!isOutOfStock || ignoreStock ? (
                              <QuantityStepper
                                value={cellQty}
                                onChange={val => handleCellQtyChange(key, val, cellMaxQty)}
                                min={0}
                                max={cellMaxQty}
                                size="sm"
                              />
                            ) : (
                              <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                                Out of stock
                              </span>
                            )}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-between gap-4 p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/90 rounded-b-2xl">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {totalSelectedQty > 0 ? (
              <>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{totalSelectedQty}</span> selected
                {showPricing && totalSelectedValue > 0 && (
                  <span className="ml-2 font-semibold text-green-600 dark:text-green-400">
                    {'\u20AC'}{totalSelectedValue.toFixed(2)}
                  </span>
                )}
              </>
            ) : (
              hideStockInfo ? 'Select quantities to count' : 'Select quantities to add to order'
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleBulkAdd}
              disabled={totalSelectedQty <= 0}
            >
              Add selected{totalSelectedQty > 0 ? ` (${totalSelectedQty})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
